import DataTable from "../components/common/DataTable";
import HeaderInfo from "../components/common/HeaderInfo";
import { Search } from "../components/icons";
import AllotmentModal from "../components/modals/AllotmentModal";
import LowStockModal from "../components/modals/LowStockModal";
import itmImg from "../assets/item-thumbnail.png";
import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import toast from "react-hot-toast";
import {
  useGetCampusSideInventoryQuery,
  useGetCategoriesQuery,
  useAllotItemToCampusMutation,
  useGetMeQuery
} from "../store/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "lucide-react";

function formatDate(dateString) {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-GB");
}

export default function CampusInventory() {
  const [lowStockOpen, setLowStockOpen] = useState(false);
  const [allotmentOpen, setAllotmentOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data: userData } = useGetMeQuery();
  const currentUserCampusId = userData?.data?.campus?._id || userData?.data?.campus || "";

  const [allotItem, { isLoading: isAllotting }] = useAllotItemToCampusMutation();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCategory, fromDate, toDate, limit]);

  const { data, isLoading, isFetching } = useGetCampusSideInventoryQuery({
    campusId: currentUserCampusId,
    page,
    limit,
    ...(debouncedSearch && { search: debouncedSearch }),
    category: selectedCategory,
    fromDate,
    toDate,
  }, { skip: !currentUserCampusId });

  const { data: categoriesData } = useGetCategoriesQuery();
  const categories = categoriesData?.data?.categories || [];

  const rows = data?.data?.data || data?.data || [];
  const total = data?.total || data?.data?.total || rows.length;
  const totalPages = limit === "All" ? 1 : Math.max(1, Math.ceil(total / limit));

  const handleGenerateReport = () => {
    if (rows.length === 0) {
      toast.error("No data available to generate report");
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Campus Inventory Report", 14, 22);

    const tableColumn = ["Item Name", "Category", "Stock", "Received On"];
    const tableRows = rows.map((r) => {
      const itemName = r?.itemName || r?.item?.name || r?.itemId?.name || "N/A";
      const categoryName = r?.itemCategory || r?.category?.name || r?.itemId?.itemCategory?.name || "N/A";
      const stock = r?.stock ?? 0;
      const date = formatDate(r.createdAt);
      return [itemName, categoryName, stock, date];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      theme: "striped",
    });

    doc.save(`Campus_Inventory_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const renderRow = (r, idx) => {
    const stock = r?.stock ?? 0;
    const itemName = r?.itemName || r?.item?.name || r?.itemId?.name || "N/A";
    const categoryName = r?.itemCategory || r?.category?.name || r?.itemId?.itemCategory?.name || "N/A";
    const itemImage = r?.itemPicture || r?.item?.itemPicture?.secure_url || r?.itemId?.itemPicture?.secure_url || itmImg;

    return (
      <div
        key={r._id || idx}
        className={`bg-[#EEEEEE40] ${idx === rows.length - 1 ? "pb-28" : ""}`}
      >
        <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5] ">
          <div className="col-span-3 flex items-center gap-[14px] text-black">
            <div className="w-[53px] h-[42px] overflow-hidden">
              <img
                src={itemImage}
                alt={itemName}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-black capitalize">{itemName}</p>
              <p className="text-[#279B9F] text-[10px] leading-[13px] capitalize">
                {categoryName}
              </p>
            </div>
          </div>

          <div className="col-span-3 text-center">
            <div
              className={`${stock === 0
                ? "bg-[#FFE1C7] text-[#CA0707] px-[21px]"
                : "bg-[#E5FFF6] px-[17px] text-[#2C765C]"
                } inline-block py-1 rounded-full font-bold text-xs`}
            >
              {stock} <span className="font-normal">Units</span>
            </div>
          </div>

          <div className="col-span-4 text-center">
            <p className="text-[#878686]">{formatDate(r.createdAt)}</p>
          </div>

          <div className="col-span-1">
            <button
              aria-label="allot"
              onClick={() => {
                setSelectedItem({
                  name: itemName,
                  stock,
                  campusItemId: r?._id,
                });
                setAllotmentOpen(true);
              }}
              className="cursor-pointer px-3 inline-flex gap-1 items-center text-white rounded-full bg-[#62A7C5]"
            >
              <Plus size={14} /> <span>Allot</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-14 pl-8 pr-12 min-h-screen">
      <div className="flex justify-between items-start">
        <HeaderInfo
          title="Campus Inventory Explorer"
          desc="Manage localized stock and current branch availability"
        />
        <div className="flex gap-3">
          <button
            onClick={() => setLowStockOpen(true)}
            className="flex items-center gap-2 bg-[#6D4E8A] text-white px-4 py-2 rounded-[8px] text-sm font-medium hover:bg-[#5a3f75] transition-colors"
          >
            View Low Stock
          </button>
          <button
            onClick={handleGenerateReport}
            className="flex items-center gap-2 bg-[#279B9F] text-white px-4 py-2 rounded-[8px] text-sm font-medium hover:bg-[#1f7a7d] transition-colors"
          >
            <Download size={16} /> Generate Report
          </button>
        </div>
      </div>

      <div className="rounded-[10px] border border-[#D9D9D9] mt-4 pt-6 ">
        <div className="pl-5 pr-6">
          <div className=" border border-[#D9D9D9] rounded-[10px] py-4 pl-6 pr-4  flex flex-wrap items-center gap-4">
            <div className="flex items-center flex-1 min-w-[250px] gap-2 py-3 px-4 bg-[#EEEEEE40] rounded-[10px]">
              <Search />
              <input
                type="text"
                name="campus-inventory-filter"
                autoComplete="off"
                placeholder="Search assets by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent outline-none text-black placeholder:text-[#878686]"
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm min-w-[200px] capitalize"
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c._id} value={c._id} className="capitalize">{c.name}</option>
              ))}
            </select>

            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm"
              />
              <span className="text-[#878686]">to</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm"
              />
            </div>

            <select
              value={limit}
              onChange={(e) => setLimit(e.target.value === "All" ? "All" : Number(e.target.value))}
              className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value="All">Show All</option>
            </select>

            <button
              onClick={() => {
                setSearch("");
                setDebouncedSearch("");
                setSelectedCategory("");
                setFromDate("");
                setToDate("");
                setLimit(10);
                setPage(1);
              }}
              className="border border-[#D9D9D9] rounded-[8px] py-3 px-4 bg-[#EEEEEE40] text-[#878686] outline-none text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Reset Filters
            </button>
          </div>
        </div>
        <div className="mt-4">
          {isLoading || isFetching ? (
            <p className="px-7 py-6 text-sm text-[#6B7280]">
              Loading campus inventory...
            </p>
          ) : rows.length === 0 ? (
            <p className="px-7 py-6 text-sm text-[#6B7280]">
              No inventory items found.
            </p>
          ) : (
            <DataTable
              columns={[
                {
                  key: "productInfo",
                  label: "Product Info",
                  className: "col-span-3",
                },
                {
                  key: "stock",
                  label: "On-Hand Stock",
                  className: "col-span-3 text-center",
                },
                {
                  key: "date",
                  label: "Received On",
                  className: "col-span-4 text-center",
                },
                {
                  key: "actions",
                  label: "Action",
                  className: "col-span-1 text-center",
                },
              ]}
              rows={rows}
              renderRow={renderRow}
              page={page}
              totalPages={totalPages}
              onNext={() => setPage((p) => Math.min(p + 1, totalPages))}
              onPrev={() => setPage((p) => Math.max(p - 1, 1))}
            />
          )}
        </div>
      </div>

      <LowStockModal
        open={lowStockOpen}
        onClose={() => setLowStockOpen(false)}
        type="campus"
        campusId={currentUserCampusId}
      />

      <AllotmentModal
        open={allotmentOpen}
        isLoading={isAllotting}
        onClose={() => {
          setAllotmentOpen(false);
          setSelectedItem(null);
        }}
        itemData={selectedItem || {}}
        onSubmit={async (data) => {
          try {
            const response = await allotItem(data).unwrap();
            if (response?.success === false) {
              toast.error(response?.message || "Failed to allot item");
              return;
            }
            toast.success("Item allotted successfully");
            setAllotmentOpen(false);
            setSelectedItem(null);
          } catch (error) {
            toast.error(error?.data?.message || error.message || "Failed to allot item");
          }
        }}
      />
    </div>
  );
}
